import type {
  ItemWithAuthorizedActions,
  UserItemAction,
  UserModelAction,
  UserModelActionType,
} from "@/entities/action";
import type { UserEntity } from "@/entities/user";
import type { ModelKey } from "@/modules/model";

import { useGetCurrentUser } from "@/entities/current-user";
import { getModelAuthorizationKey } from "@/modules/model";

export const useAuthorization = () => {
  const { currentUser } = useGetCurrentUser();

  const isModelAuthorizedAction = (
    action: UserModelActionType,
    modelKey: ModelKey
  ): boolean => {
    const modelAuthorizationKey = getModelAuthorizationKey(modelKey, action);

    // When `null` value, there is no special authorization in api
    // Which means, user has the authorization !
    if (modelAuthorizationKey === null) return true;

    if (!currentUser) return false;

    return currentUser._authorizedActions?.includes(
      `${action}.${modelAuthorizationKey}` as UserModelAction
    );
  };

  const isItemAuthorizedAction = (
    action: UserItemAction,
    item: ItemWithAuthorizedActions
  ): boolean => {
    if (!item?._authorizedActions?.includes(action)) return false;

    return true;
  };

  return {
    currentUser,
    canViewModel: (modelKey: ModelKey) => {
      return isModelAuthorizedAction("view", modelKey);
    },
    canCreateModel: (modelKey: ModelKey) => {
      return isModelAuthorizedAction("create", modelKey);
    },
    canUpdateModel: (modelKey: ModelKey) => {
      return isModelAuthorizedAction("update", modelKey);
    },
    canUpdateItem: (item: ItemWithAuthorizedActions) => {
      return isItemAuthorizedAction("update", item);
    },
    canDeleteItem: (item: ItemWithAuthorizedActions) => {
      return isItemAuthorizedAction("delete", item);
    },
    canResetUser: (user: UserEntity) => {
      if (!isItemAuthorizedAction("reset.user", user)) return false;
      if (!user.authentication?.can_mfa_be_reset) return false;
      return true;
    },
    canResendInvite: (user: UserEntity) => {
      if (!isItemAuthorizedAction("resendInvite", user)) return false;
      if (!user.authentication?.can_invite_be_resent) return false;
      return true;
    },
    canForceChangePassword: (user: UserEntity) => {
      if (!isItemAuthorizedAction("forceChangePassword", user)) return false;
      if (!user.authentication?.can_password_be_reset) return false;
      return true;
    },
    canManageRole: (item: ItemWithAuthorizedActions) => {
      return isItemAuthorizedAction("manageRole", item);
    },
  };
};
